// One traveller's public timeline, read from GET /users/:id/timeline.
//
// Used from two places: tapping an author in Explore, and tapping a row in
// the "who is at this pin" sheet on the map. Nothing here is trip scoped:
// these are strangers with unrelated trips.
import { useEffect, useState } from "react";
import { Icon, IconBook } from "@traveldiary/ui";
import { apiClient } from "../lib/api";
import { EmptyState } from "./EmptyState";
import { TravellerAvatar } from "./TravellerAvatar";
import { MomentRow } from "./MomentRow";
import type { AuthoredMoment, PublicUser } from "../lib/types";
import { useApp } from "../state/app";

export interface TravellerTimelineProps {
  user: PublicUser;
  onOpenMoment?(entry: AuthoredMoment): void;
}

export function TravellerTimeline({ user, onOpenMoment }: TravellerTimelineProps) {
  const { t } = useApp();
  const [entries, setEntries] = useState<AuthoredMoment[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setFailed(false);
    void apiClient
      .userTimeline(user.id)
      .then((list) => {
        if (cancelled) return;
        setEntries(list);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  return (
    <div className="timeline">
      <div className="timeline-head">
        <TravellerAvatar user={user} size="md" />
        <span className="timeline-head-text">
          <span className="timeline-name">{user.displayName}</span>
          <span className="timeline-handle">{user.username}</span>
        </span>
      </div>

      {failed ? (
        <p className="field-note" role="status">
          {t("explore.timelineFailed")}
        </p>
      ) : null}

      {entries === null && !failed ? (
        <p className="field-note" role="status">
          {t("common.loading")}
        </p>
      ) : null}

      {entries !== null && entries.length === 0 ? (
        <EmptyState
          glyph={<IconBook />}
          title={t("explore.timelineEmpty")}
          hint={t("explore.timelineEmptyHint")}
        />
      ) : null}

      {(entries ?? []).map((entry) => (
        <MomentRow key={entry.id} entry={entry} onOpenMoment={onOpenMoment} />
      ))}

      {entries !== null && entries.length > 0 ? (
        <p className="timeline-foot">
          <Icon size={14} color="var(--color-text-muted)">
            <IconBook />
          </Icon>
          {entries.length} {t("explore.entries")}
        </p>
      ) : null}
    </div>
  );
}
